import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Alert } from 'components'
import { getAuthUserData } from 'utils/authManager'
import {
  subscribeToIncomingRingingCalls,
  acceptCall,
  endCall
} from '../../services/chat/firebaseChat'

const useRingtone = () => {
  const ringRef = useRef(null) // { stop() }

  const stop = () => {
    try {
      ringRef.current?.stop?.()
    } catch (_) {}
    ringRef.current = null
  }

  const start = () => {
    if (ringRef.current) return
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const gain = ctx.createGain()
      gain.gain.value = 0.12
      gain.connect(ctx.destination)

      let stopped = false
      const playPattern = () => {
        if (stopped) return
        const t0 = ctx.currentTime + 0.02
        const osc = ctx.createOscillator()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(880, t0)
        osc.connect(gain)

        osc.start(t0)
        osc.stop(t0 + 0.18)

        const osc2 = ctx.createOscillator()
        osc2.type = 'sine'
        osc2.frequency.setValueAtTime(880, t0 + 0.24)
        osc2.connect(gain)
        osc2.start(t0 + 0.24)
        osc2.stop(t0 + 0.42)

        const next = setTimeout(playPattern, 1600)
        ringRef.current = {
          stop: () => {
            stopped = true
            clearTimeout(next)
            try {
              osc.stop()
            } catch (_) {}
            try {
              osc2.stop()
            } catch (_) {}
            try {
              ctx.close()
            } catch (_) {}
          }
        }
      }

      ctx.resume?.().catch(() => {})
      playPattern()
    } catch (_) {
      // Ignore audio failures (autoplay restrictions, etc.)
    }
  }

  return { start, stop }
}

export default function IncomingCallListener() {
  const navigate = useNavigate()
  const location = useLocation()
  const { start: startRing, stop: stopRing } = useRingtone()

  const currentUserId = useMemo(() => {
    const u = getAuthUserData() || {}
    return (u._id || u.userRefId || u.id)?.toString() || null
  }, [])

  const [incoming, setIncoming] = useState(null) // { channelId, channelName, activeCall }

  useEffect(() => {
    if (!currentUserId) return
    const unsub = subscribeToIncomingRingingCalls(calls => {
      // Pick the most recent ringing call (best effort)
      const sorted = (calls || [])
        .filter(x => x?.activeCall?.status === 'ringing')
        .sort((a, b) => (b.activeCall?.createdAt?.seconds || 0) - (a.activeCall?.createdAt?.seconds || 0))
      setIncoming(sorted[0] || null)
    })
    return () => unsub && unsub()
  }, [currentUserId])

  useEffect(() => {
    const isOnCallRoom = location.pathname.startsWith('/userAdmin/chat/call/')
    const shouldRing =
      !isOnCallRoom &&
      incoming &&
      incoming.activeCall &&
      incoming.activeCall.status === 'ringing' &&
      String(incoming.activeCall.createdBy) !== String(currentUserId)

    if (shouldRing) startRing()
    else stopRing()

    return () => stopRing()
  }, [incoming, currentUserId, location.pathname, startRing, stopRing])

  if (!incoming?.activeCall) return null
  const { channelId, channelName, activeCall } = incoming

  // Don't show modal while already on call room
  if (location.pathname.startsWith('/userAdmin/chat/call/')) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 9999
      }}
      onClick={() => {}}
    >
      <div
        style={{
          width: 'min(92vw, 420px)',
          background: '#fff',
          borderRadius: 14,
          padding: 16,
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontWeight: 800, fontSize: 16 }}>
          Incoming {activeCall.type === 'video' ? 'video' : 'voice'} call
        </div>
        <div style={{ opacity: 0.75, marginTop: 4, fontSize: 13 }}>
          {channelName || 'Chat'}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
          <button
            type='button'
            style={{
              background: '#ef4444',
              border: 0,
              color: '#fff',
              borderRadius: 999,
              padding: '10px 14px',
              cursor: 'pointer'
            }}
            onClick={async () => {
              try {
                await endCall(channelId, activeCall.id, { reason: 'rejected' })
              } catch (_) {}
            }}
          >
            Decline
          </button>
          <button
            type='button'
            style={{
              background: '#22c55e',
              border: 0,
              color: '#fff',
              borderRadius: 999,
              padding: '10px 14px',
              cursor: 'pointer'
            }}
            onClick={async () => {
              try {
                await acceptCall(channelId, activeCall.id)
                navigate(`/userAdmin/chat/call/${channelId}/${activeCall.id}`)
              } catch (err) {
                Alert.error(err.message || 'Failed to accept call')
              }
            }}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  )
}

